import {Component, OnDestroy, OnInit} from '@angular/core';

import {ModuleModel, NavigationModel} from '@cas-hub/acabim-common-client';
import {
    faAddressBook,
    faBalanceScale,
    faDesktop,
    faDollarSign,
    faLandmark,
    faUser,
    faUserCircle,
    faUsers,
    faWrench,
    faCubes,
    faBoxes,
    faBusinessTime,
    faPlayCircle,
    faFile
} from '@fortawesome/free-solid-svg-icons';

@Component({
    template: `
        <acabim-main-layout [navItems]="navItems" [module]="module"></acabim-main-layout>`
})
export class AcabimAdminLayoutComponent implements OnInit, OnDestroy {

    public readonly module: ModuleModel = {
        title: 'Admin',
        description: 'Admin, Settings and Configurations',
        key: 'admin',
        topNav: true,
        projectShow: false,
        projectOpen: false,
        projectSelect: false,
        projectLabel: 'Building'
    };

    public navItems: NavigationModel[] = [
        {
            title: 'Dashboard',
            link: '/admin/home',
            icon: faDesktop,
            dividerBottom: true
        },
        {
            title: 'Manage Users',
            link: '/user',
            icon: faUser
        },
        {
            title: 'Manage Organisation',
            link: '/admin/organisation',
            permissions: ['admin'],
            icon: faUserCircle,
            dividerBottom: true,
            children: [
                {
                    title: 'Preferences/Settings',
                    link: '/admin/organisation/preferences',
                    icon: faUserCircle,
                    permissions: ['admin']
                }
            ]
        },
        {
            title: 'Manage Sites/Projects',
            link: '/admin/sites-projects',
            icon: faLandmark,
            permissions: ['manageSites', 'manageProjects'],
            children: [
                {
                    title: 'Sites/Projects Grouping',
                    link: '/admin/sites-projects/groups',
                    permissions: ['manageSites', 'manageProjects']
                }]
        },
        {
            title: 'Manage BIM',
            link: '/admin/bim',
            ghost: true,
            icon: faCubes,
            permissions: ['manageBIMs'],
            children: [
                {
                    title: 'Manage BIM Model',
                    link: '/admin/bim/bim-model-management',
                    permissions: ['manageBIMs']
                },
                {
                    title: 'Manage Building',
                    link: '/admin/bim/building-management',
                    permissions: ['manageBIMs']
                },
                {
                    title: 'Manage Space',
                    link: '/admin/bim/space-management',
                    expanded: true,
                    permissions: ['manageBIMs'],
                    children: [
                        {
                            title: 'Space Grouping',
                            link: '/admin/bim/space-management/space-grouping',
                            permissions: ['manageBIMs']
                        }
                    ]
                },
                {
                    title: 'User Property Sets',
                    link: '/admin/bim/user-property-sets',
                    permissions: ['manageBIMs']
                },
                {
                    title: 'BIM Data Export',
                    link: '/admin/bim/data-export',
                    permissions: ['manageBIMs']
                }
            ]
        },
        {
            title: 'Manage Actions',
            link: '/admin/actions',
            icon: faBusinessTime,
            permissions: ['admin']
        },
        {
            title: 'Manage Assets',
            link: '/admin/assets',
            permissions: ['manageAssets'],
            dividerTop: false,
            dividerBottom: true,
            icon: faBoxes,
            children: [
                {
                    title: 'Asset Grouping',
                    link: '/admin/assets/asset-groups',
                    permissions: ['manageAssets']
                },
                {
                    title: 'Maintenance Types',
                    link: '/admin/assets/maintenance-types',
                    permissions: ['manageAssets']
                },
                {
                    title: 'LTM Items',
                    link: '/admin/assets/long-term-maintenance-items',
                    permissions: ['manageAssets']
                }
            ]
        },
        {
            title: 'Manage CAP/LKM',
            link: '/admin/cap',
            permissions: ['superOnly'],
            dividerTop: false,
            dividerBottom: true,
            ghost: true,
            icon: faBalanceScale,
            children:
                [
                    {
                        title: 'Manage CAP Library',
                        link: '/admin/cap/library',
                        permissions: ['superOnly']
                    },
                    {
                        title: 'Assign CAP to Project',
                        link: '/admin/cap/cap-in-project',
                        permissions: ['superOnly']
                    },
                    {
                        title: 'Manage LKM',
                        superAdminOnly: true,
                        link: '/admin/cap/lkm',
                        permissions: ['superOnly']
                    }]
        },
        {
            title: 'Financial Settings',
            link: '/admin/financial',
            permissions: ['admin'],
            ghost: true,
            icon: faDollarSign,
            children: [
                {
                    title: 'Preferences/Settings',
                    link: '/admin/financial/settings',
                    permissions: ['admin']
                },
                {
                    title: 'Chart of Accounts',
                    link: '/admin/financial/accounts',
                    permissions: ['admin']
                },
                {
                    title: 'Account Classes',
                    link: '/admin/financial/manage-account-classes',
                    permissions: ['admin']
                },
                {
                    title: 'Inventory',
                    link: '/admin/financial/inventory',
                    permissions: ['admin']
                }
            ]
        },
        {
            title: 'Work Order Settings',
            icon: faWrench,
            permissions: ['manageWorkOrder'],
            dividerTop: true,
            link: '/admin/work',
            ghost: true,
            children: [
                {
                    title: 'Work Types',
                    link: '/admin/work/work-types',
                    permissions: ['manageWorkOrder']
                }
            ]
        },
         {
            title: 'Document Settings',
            icon: faFile,
            permissions: ['manageDocument'],
            dividerTop: true,
            link: '/admin/document',
            ghost: true,
            children: [
                {
                    title: 'Document Types',
                    link: '/admin/document/document-types',
                    permissions: ['manageDocument']
                }
            ]
        },
        {
            title: 'Manage Contacts',
            link: '/admin/contacts',
            permissions: ['manageContacts'],
            icon: faAddressBook,
            children: [
                {
                    title: 'Contact types',
                    link: '/admin/contacts/types',
                    permissions: ['manageContacts']
                }]
        },
        {
            title: 'Subscriptions',
            link: '/admin/subscriptions',
            icon: faUsers,
            dividerTop: true,
            permissions: ['superOnly'],
            children: [
                {
                    title: 'Pricing',
                    link: '/admin/subscriptions/pricing',
                    permissions: ['superOnly'],
                }
            ]
        },
        {
            title: 'Run Action Tasks',
            link: '/admin/tasks',
            icon: faPlayCircle,
            permissions: ['superOnly']
        }
    ];

    public constructor() {}

    public ngOnInit(): void {}

    public ngOnDestroy(): void {}
}
